import { z } from 'zod';
import { CATEGORIAS_CNH, cepValido, cnhValida, cnpjValido, cpfValido, normalizarPlaca, placaValida, renavamValido, rntrcValido, UFS, } from '../br/documentos.js';
import { phoneSchema } from './auth.js';
/**
 * Schemas das Ondas 2 e 3, compartilhados entre API e formularios.
 *
 * Regra que se repete aqui: toda validacao que o usuario precisa entender vive
 * neste arquivo, com a mensagem em portugues, para que API e UI recusem a mesma
 * coisa pelo mesmo motivo e com o mesmo texto.
 */
// ═══════════════════════════════════════════════════════════ enumeracoes
export const TIPOS_CARROCERIA = [
    'BAU',
    'SIDER',
    'GRANELEIRA',
    'FRIGORIFICA',
    'PRANCHA',
    'TANQUE',
    'CACAMBA',
    'PORTA_CONTAINER',
    'CEGONHA',
];
export const ROTULO_CARROCERIA = {
    BAU: 'Baú',
    SIDER: 'Sider',
    GRANELEIRA: 'Graneleira',
    FRIGORIFICA: 'Frigorífica',
    PRANCHA: 'Prancha',
    TANQUE: 'Tanque',
    CACAMBA: 'Caçamba',
    PORTA_CONTAINER: 'Porta-contêiner',
    CEGONHA: 'Cegonha',
};
export const TIPOS_CARGA = [
    'GERAL',
    'GRANEL_SOLIDO',
    'GRANEL_LIQUIDO',
    'FRIGORIFICADA',
    'PERIGOSA',
    'VIVA',
    'VEICULOS',
    'MUDANCA',
    'CONTAINER',
];
export const ROTULO_CARGA = {
    GERAL: 'Carga geral',
    GRANEL_SOLIDO: 'Granel sólido',
    GRANEL_LIQUIDO: 'Granel líquido',
    FRIGORIFICADA: 'Frigorificada',
    PERIGOSA: 'Carga perigosa',
    VIVA: 'Carga viva',
    VEICULOS: 'Veículos',
    MUDANCA: 'Mudança',
    CONTAINER: 'Contêiner',
};
/**
 * Carroceria compativel com cada tipo de carga.
 *
 * Nao e sugestao: a API recusa publicar frigorificada exigindo graneleira. Sem
 * isso, o frete iria para a vitrine e o motorista descobriria a incompatibilidade
 * no ponto de coleta, com o caminhao ja no lugar.
 */
export const CARROCERIA_POR_CARGA = {
    GERAL: ['BAU', 'SIDER', 'GRANELEIRA', 'PRANCHA'],
    GRANEL_SOLIDO: ['GRANELEIRA', 'CACAMBA'],
    GRANEL_LIQUIDO: ['TANQUE'],
    FRIGORIFICADA: ['FRIGORIFICA'],
    PERIGOSA: ['TANQUE', 'SIDER', 'BAU', 'CACAMBA'],
    VIVA: ['GRANELEIRA', 'SIDER'],
    VEICULOS: ['CEGONHA', 'PRANCHA'],
    MUDANCA: ['BAU', 'SIDER'],
    CONTAINER: ['PORTA_CONTAINER', 'PRANCHA'],
};
export const MODOS_PRECO = ['FIXED', 'PER_KM', 'NEGOTIABLE'];
export const ROTULO_MODO_PRECO = {
    FIXED: 'Valor fixo',
    PER_KM: 'Por quilômetro',
    NEGOTIABLE: 'A combinar',
};
export const VISIBILIDADES = ['PUBLIC', 'ORG_ONLY', 'INVITE_ONLY'];
export const STATUS_FRETE = [
    'DRAFT',
    'PUBLISHED',
    'RESERVED',
    'ASSIGNED',
    'IN_TRANSIT',
    'DELIVERED',
    'COMPLETED',
    'CANCELLED',
    'EXPIRED',
];
export const ROTULO_STATUS_FRETE = {
    DRAFT: 'Rascunho',
    PUBLISHED: 'Publicado',
    RESERVED: 'Pré-reservado',
    ASSIGNED: 'Motorista definido',
    IN_TRANSIT: 'Em trânsito',
    DELIVERED: 'Entregue',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    EXPIRED: 'Expirado',
};
export const STATUS_VERIFICACAO = [
    'PENDING',
    'UNDER_REVIEW',
    'APPROVED',
    'REJECTED',
    'EXPIRED',
];
export const ROTULO_VERIFICACAO = {
    PENDING: 'Cadastro incompleto',
    UNDER_REVIEW: 'Em análise',
    APPROVED: 'Aprovado',
    REJECTED: 'Recusado',
    EXPIRED: 'Documento vencido',
};
// ═══════════════════════════════════════════════════════════ primitivas
export const cpfSchema = z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine(cpfValido, 'CPF inválido — confira os dígitos');
export const cnpjSchema = z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine(cnpjValido, 'CNPJ inválido — confira os dígitos');
export const placaSchema = z
    .string()
    .transform(normalizarPlaca)
    .refine(placaValida, 'Placa inválida. Use ABC1234 ou ABC1D23 (Mercosul)');
export const cepSchema = z
    .string()
    .transform((v) => v.replace(/\D/g, ''))
    .refine(cepValido, 'CEP deve ter 8 dígitos');
export const ufSchema = z
    .string()
    .transform((v) => v.toUpperCase())
    .refine((v) => UFS.includes(v), 'UF inválida');
/** Data futura. Documento vencido nao entra no cadastro. */
const dataFuturaSchema = z.coerce
    .date()
    .refine((d) => d.getTime() > Date.now(), 'A data precisa estar no futuro');
export const coordenadaSchema = z.object({
    lat: z.number().min(-34).max(6, 'Latitude fora do território brasileiro'),
    lon: z.number().min(-74).max(-33, 'Longitude fora do território brasileiro'),
});
// ═══════════════════════════════════════════════════ Onda 2 — motorista
export const cadastroMotoristaSchema = z.object({
    cpf: cpfSchema,
    cnhNumber: z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine(cnhValida, 'Número de CNH inválido — são 11 dígitos'),
    cnhCategory: z.enum(CATEGORIAS_CNH, {
        errorMap: () => ({ message: 'Selecione a categoria da sua CNH' }),
    }),
    cnhExpiresAt: dataFuturaSchema,
    cnhFileId: z.string().uuid().optional(),
    hasMopp: z.boolean().default(false),
    moppExpiresAt: z.coerce.date().optional(),
    moppFileId: z.string().uuid().optional(),
})
    // MOPP marcado sem validade nao serve para nada: o job de vencimento nao
    // saberia quando avisar, e a carga perigosa seria liberada indefinidamente.
    .refine((d) => !d.hasMopp || d.moppExpiresAt !== undefined, {
    message: 'Informe a validade do curso MOPP',
    path: ['moppExpiresAt'],
})
    .refine((d) => !d.moppExpiresAt || d.moppExpiresAt.getTime() > Date.now(), {
    message: 'O curso MOPP está vencido',
    path: ['moppExpiresAt'],
});
export const baseOperacionalSchema = z.object({
    homeBaseLabel: z.string().trim().min(4, 'Informe o endereço da sua base').max(200),
    homeBaseCity: z.string().trim().min(2).max(120),
    homeBaseState: ufSchema,
    lat: coordenadaSchema.shape.lat,
    lon: coordenadaSchema.shape.lon,
    serviceRadiusKm: z.coerce
        .number()
        .int()
        .min(20, 'O raio mínimo é 20 km')
        .max(3000, 'O raio máximo é 3.000 km')
        .default(300),
});
export const veiculoSchema = z.object({
    plate: placaSchema,
    renavam: z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v === '' || renavamValido(v), 'RENAVAM inválido')
        .optional(),
    brandModel: z.string().trim().max(120).optional(),
    year: z.coerce
        .number()
        .int()
        .min(1970, 'Ano muito antigo')
        .max(new Date().getFullYear() + 1)
        .optional(),
    bodyType: z.enum(TIPOS_CARROCERIA, {
        errorMap: () => ({ message: 'Selecione o tipo de carroceria' }),
    }),
    axles: z.coerce.number().int().min(2, 'Mínimo 2 eixos').max(9, 'Máximo 9 eixos'),
    capacityKg: z.coerce
        .number()
        .int()
        .min(100, 'Capacidade mínima 100 kg')
        .max(80000, 'Capacidade acima do limite legal'),
    capacityM3: z.coerce.number().positive().max(200).optional(),
    hasTracker: z.boolean().default(false),
    crlvFileId: z.string().uuid().optional(),
    crlvExpiresAt: z.coerce.date().optional(),
});
export const disponibilidadeSchema = z.object({
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    originLabel: z.string().trim().min(3).max(200),
    originLat: coordenadaSchema.shape.lat,
    originLon: coordenadaSchema.shape.lon,
    destinationLabel: z.string().trim().max(200).optional(),
    destinationLat: coordenadaSchema.shape.lat.optional(),
    destinationLon: coordenadaSchema.shape.lon.optional(),
    corridorKm: z.coerce.number().int().min(5).max(500).default(50),
})
    .refine((d) => d.startsAt < d.endsAt, {
    message: 'O fim precisa ser depois do início',
    path: ['endsAt'],
})
    .refine((d) => d.endsAt.getTime() > Date.now(), {
    message: 'Este período já passou',
    path: ['endsAt'],
})
    // Destino pela metade quebraria o corredor: o score usa a LINHA origem→destino.
    .refine((d) => (d.destinationLat === undefined) === (d.destinationLon === undefined), { message: 'Informe as duas coordenadas do destino, ou nenhuma', path: ['destinationLat'] });
export const cadastroEmbarcadorSchema = z
    .object({
    isCompany: z.boolean(),
    // PF
    cpf: cpfSchema.optional(),
    // PJ
    legalName: z.string().trim().min(3).max(200).optional(),
    tradeName: z.string().trim().max(200).optional(),
    cnpj: cnpjSchema.optional(),
    anttRntrc: z
        .string()
        .transform((v) => v.replace(/\D/g, ''))
        .refine((v) => v === '' || rntrcValido(v), 'RNTRC inválido — são 8 dígitos')
        .optional(),
})
    .refine((d) => !d.isCompany || (d.cnpj && d.legalName), {
    message: 'Pessoa jurídica exige CNPJ e razão social',
    path: ['cnpj'],
})
    .refine((d) => d.isCompany || d.cpf, {
    message: 'Pessoa física exige CPF',
    path: ['cpf'],
});
export const decisaoVerificacaoSchema = z
    .object({
    decisao: z.enum(['APROVAR', 'RECUSAR']),
    motivo: z.string().trim().max(600).optional(),
    /// Quais campos precisam ser reenviados, para a UI pedir so o que falta.
    camposRecusados: z.array(z.string()).default([]),
})
    // Recusa sem motivo obriga o motorista a adivinhar o que corrigir — e o
    // principal gerador de reenvio errado e de contato com o suporte.
    .refine((d) => d.decisao === 'APROVAR' || (d.motivo && d.motivo.length >= 10), {
    message: 'Explique o motivo da recusa em pelo menos 10 caracteres',
    path: ['motivo'],
});
// ═══════════════════════════════════════════════════════ Onda 3 — frete
export const paradaSchema = z.object({
    kind: z.enum(['PICKUP', 'DELIVERY']),
    addressLine: z.string().trim().min(5, 'Informe o endereço').max(250),
    district: z.string().trim().max(120).optional(),
    city: z.string().trim().min(2, 'Informe a cidade').max(120),
    state: ufSchema,
    zipCode: cepSchema.optional(),
    lat: coordenadaSchema.shape.lat,
    lon: coordenadaSchema.shape.lon,
    contactName: z.string().trim().max(120).optional(),
    contactPhone: phoneSchema.optional(),
    scheduledAt: z.coerce.date().optional(),
    notes: z.string().trim().max(500).optional(),
});
export const freteSchema = z
    .object({
    title: z.string().trim().min(6, 'Dê um título que descreva a carga').max(160),
    notes: z.string().trim().max(2000).optional(),
    cargoType: z.enum(TIPOS_CARGA),
    requiredBody: z.array(z.enum(TIPOS_CARROCERIA)).default([]),
    weightKg: z.coerce.number().int().min(1, 'Informe o peso').max(80000, 'Peso acima do limite legal'),
    volumeM3: z.coerce.number().positive().max(500).optional(),
    isDangerous: z.boolean().default(false),
    requiresTracker: z.boolean().default(false),
    minAxles: z.coerce.number().int().min(2).max(9).optional(),
    pickupWindowStart: z.coerce.date(),
    pickupWindowEnd: z.coerce.date(),
    deliveryWindowStart: z.coerce.date(),
    deliveryWindowEnd: z.coerce.date(),
    isRecurring: z.boolean().default(false),
    recurrenceRule: z.string().trim().max(200).optional(),
    pricingMode: z.enum(MODOS_PRECO),
    priceCents: z.coerce.number().int().positive().optional(),
    pricePerKmCents: z.coerce.number().int().positive().optional(),
    tollIncluded: z.boolean().default(false),
    advancePct: z.coerce.number().int().min(0).max(100).default(0),
    paymentTermDays: z.coerce.number().int().min(0).max(180).default(30),
    visibility: z.enum(VISIBILIDADES).default('PUBLIC'),
    expiresAt: z.coerce.date().optional(),
    stops: z
        .array(paradaSchema)
        .min(2, 'Um frete precisa de ao menos uma coleta e uma entrega')
        .max(20, 'Máximo de 20 paradas'),
})
    // ---- coerência das janelas ----
    .refine((d) => d.pickupWindowStart <= d.pickupWindowEnd, {
    message: 'O fim da janela de coleta precisa ser depois do início',
    path: ['pickupWindowEnd'],
})
    .refine((d) => d.deliveryWindowStart <= d.deliveryWindowEnd, {
    message: 'O fim da janela de entrega precisa ser depois do início',
    path: ['deliveryWindowEnd'],
})
    .refine((d) => d.pickupWindowStart <= d.deliveryWindowEnd, {
    message: 'A entrega não pode terminar antes de a coleta começar',
    path: ['deliveryWindowEnd'],
})
    .refine((d) => d.pickupWindowEnd.getTime() > Date.now(), {
    message: 'A janela de coleta já passou',
    path: ['pickupWindowStart'],
})
    // ---- preço coerente com o modo ----
    .refine((d) => d.pricingMode !== 'FIXED' || (d.priceCents ?? 0) > 0, {
    message: 'Informe o valor do frete',
    path: ['priceCents'],
})
    .refine((d) => d.pricingMode !== 'PER_KM' || (d.pricePerKmCents ?? 0) > 0, {
    message: 'Informe o valor por quilômetro',
    path: ['pricePerKmCents'],
})
    // ---- paradas ----
    .refine((d) => d.stops.some((s) => s.kind === 'PICKUP'), {
    message: 'Inclua ao menos um ponto de coleta',
    path: ['stops'],
})
    .refine((d) => d.stops.some((s) => s.kind === 'DELIVERY'), {
    message: 'Inclua ao menos um ponto de entrega',
    path: ['stops'],
})
    // ---- carga perigosa ----
    .refine((d) => !d.isDangerous || d.cargoType === 'PERIGOSA', {
    message: 'Marque o tipo de carga como "Carga perigosa"',
    path: ['cargoType'],
})
    // ---- carroceria compatível com a carga ----
    // Sem isto o frete iria para a vitrine e a incompatibilidade só apareceria
    // com o caminhão já no ponto de coleta.
    .refine((d) => d.requiredBody.length === 0 ||
    d.requiredBody.every((b) => CARROCERIA_POR_CARGA[d.cargoType].includes(b)), (d) => ({
    message: `Para ${ROTULO_CARGA[d.cargoType]}, use: ${CARROCERIA_POR_CARGA[d.cargoType]
        .map((b) => ROTULO_CARROCERIA[b])
        .join(', ')}`,
    path: ['requiredBody'],
}))
    .refine((d) => !d.isRecurring || (d.recurrenceRule && d.recurrenceRule.length > 0), {
    message: 'Defina a regra de repetição',
    path: ['recurrenceRule'],
});
/** Filtros da vitrine (SPEC 6.4). Todos opcionais e combinaveis. */
export const filtrosVitrineSchema = z.object({
    origemLat: z.coerce.number().optional(),
    origemLon: z.coerce.number().optional(),
    origemRaioKm: z.coerce.number().int().min(10).max(1000).default(150),
    destinoLat: z.coerce.number().optional(),
    destinoLon: z.coerce.number().optional(),
    destinoRaioKm: z.coerce.number().int().min(10).max(1000).default(150),
    coletaDe: z.coerce.date().optional(),
    coletaAte: z.coerce.date().optional(),
    carroceria: z.array(z.enum(TIPOS_CARROCERIA)).optional(),
    tipoCarga: z.array(z.enum(TIPOS_CARGA)).optional(),
    pesoMaxKg: z.coerce.number().int().positive().optional(),
    precoMinCents: z.coerce.number().int().nonnegative().optional(),
    porKmMinCents: z.coerce.number().int().nonnegative().optional(),
    semCargaPerigosa: z.coerce.boolean().optional(),
    adiantamentoMinPct: z.coerce.number().int().min(0).max(100).optional(),
    prazoPagamentoMaxDias: z.coerce.number().int().min(0).max(180).optional(),
    ordem: z
        .enum(['compatibilidade', 'preco_km', 'coleta_proxima', 'menor_deslocamento', 'recentes'])
        .default('compatibilidade'),
    cursor: z.string().optional(),
    limite: z.coerce.number().int().min(1).max(50).default(20),
});
//# sourceMappingURL=frete.js.map